/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState } from 'react';
import { Col, Row, Table } from 'reactstrap';
import { get } from 'lodash-es';
import { useSelector } from 'react-redux';
import axios from 'axios';

import { escapeSpecialChars } from '../../../utilities/helpers';
import { preformatFloat, formatCurrencyForLocale } from '../../../utilities/invoiceUtils';
import { awsServer, cfPath } from '../../../utilities/UrlPaths';
import { selectPolicyTerms, selectHouseID } from '../../../selectors';
import { SHIPPING_METHODS_MAP } from '../constants';

const noImage = `${awsServer}/static/noImage.png`;
const ViewInvoice = ({ invoiceID, invoiceData, isInvShippingAvailable }) => {
  const { house, houseAddress, invoices, invoiceMessages } = invoiceData;

  //  set lowercase headers
  invoiceMessages.invoiceMessages.forEach(message => {
    message.headerLC = message.header.toLowerCase();
  });
  const [showLotAddlCharges, setShowLotAddlCharges] = useState(false);
  const auctions = get(invoices, 'invoiceList[0].auctionList.auctions', []);
  const items = get(invoices, 'invoiceList[0].invoiceItems.items', []);
  const unsuppressedItems = items.filter(item => item.suppress === false);
  const isRestricted = Number(get(invoices, 'invoiceList[0].restrictedTotal', '0.00')) > 0;
  const total = invoices.invoiceList[0].invoiceTotal;
  const due = invoices.invoiceList[0].totalDue;
  const onlineTotal = invoices.invoiceList[0].onlineTotal;
  const payments = invoices.invoiceList[0].paymentsMade;
  const showCostsPerItem = get(invoices, 'invoiceList[0].showCostsPerItem', true);
  const { paymentProcessingEnabled } = useSelector(state => state.house);
  const [defaultShippingMethods, setDefaultShippingMethods] = useState('');
  const policyTerms = useSelector(selectPolicyTerms);
  const houseID = useSelector(selectHouseID);
  const [shipmentIDs, setShipmentIDs] = useState([]);
  const [shipmentsMap, setShipmentsMap] = useState({});

  useEffect(() => {
    let returnFlag = false;
    const shippingShipmentIDs = new Set();
    if (items.length) {
      items.forEach(item => {
        if (item.additionalCharge && item.additionalPriceInfo !== null) {
          returnFlag = true;
        }
        if (item.shippingShipmentID) {
          shippingShipmentIDs.add(item.shippingShipmentID);
        }
      });
    }
    if (shippingShipmentIDs.size > 0) {
      setShipmentIDs(() => Array.from(shippingShipmentIDs));
    }
    setShowLotAddlCharges(returnFlag);
  }, [items]);

  useEffect(() => {
    if (shipmentIDs.length === 0) return;
    const controller = new AbortController();
    let mounted = true;

    const fetchShipments = async () => {
      if (shipmentIDs.length === 0) return;
      const shipments = await Promise.all(shipmentIDs.map(async (shipmentID) => {
        const response = await axios.get(`/api/shipping/shipment/${shipmentID}`, {
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            'x-invaluable-shipping-account-id': `${houseID}`,
            'x-invaluable-shipping-account-domain': 'AUCTIONHOUSE'
          }
        });
        return response.data.data.shipment;
      })).catch(err => console.log('Error fetching shipments: ', err));;
      if (!shipments?.length) return;
      let shipmentIDtoShipmentMap = {};
      shipments.forEach((shipment) => {
        shipmentIDtoShipmentMap[shipment.id] = shipment;
      });
      if (mounted) setShipmentsMap(shipmentIDtoShipmentMap);
    };
    fetchShipments();
    return () => {
      mounted = false;
      controller.abort();
    }
  }, [shipmentIDs, houseID]);

  useEffect(() => {
    const defaultTermsID = auctions[0].defaultShippingTermsID;

    if (!!policyTerms && defaultTermsID) {
      const defaultTerm = policyTerms.find(term => term.id === auctions[0].defaultShippingTermsID);
      let shippingMethods = '';
      if (defaultTerm) {
        defaultTerm.shippingOptions.forEach((option, index) => {
          if (index > 0) {
            shippingMethods += ', ';
          }
          shippingMethods += SHIPPING_METHODS_MAP[option.method];
        });
        setDefaultShippingMethods(shippingMethods);
      }
    }
  }, [policyTerms, auctions]);

  const cardNameObject = {
    VISA: 'Visa',
    MC: 'Mastercard',
    DISC: 'Discover',
    AMEX: 'American Express',
  };

  const checkAndFormatCurrency = (amount) => {
    const symbol = invoices.invoiceList[0].currencyCode;
    // replace commas and spaces
    const regPattern = /[^0-9.]/g;
    const noCommas = amount.toString().replace(regPattern, '');
    const numberTest = isNaN(Number(noCommas));
    let displayAmount = `${symbol}0.00`;
    if (!numberTest) {
      displayAmount = `${symbol}${amount}`;
    }
    if (displayAmount.substring(0, 3) === `${symbol}-0`) {
      displayAmount = displayAmount.replace(`${symbol}-0`, `${symbol}0`);
    }

    return displayAmount;
  };

  const getShippingMethod = (item) => {
    if (item.shippingReviewID) {
      // it means new shipping is enabled
      if (item.shipping !== "0.00") {
        return checkAndFormatCurrency(formatCurrencyForLocale(item.shipping));
      } else if (item.shippingShipmentID) {
        const method = shipmentsMap[item.shippingShipmentID]?.arguments.option.shippingMethod;
        if (method === "invaluable_shipping") {
          return (
            <>
              <img src={`${awsServer}/static/invShippingIcon.png`} alt="shipping truck" className="shippingTruckIcon height-20" />
              <span className="pl-2">
                Inv Ship
              </span>
            </>
          );
        } else {
          return SHIPPING_METHODS_MAP[method];
        }
      } else {
        return 'Not Selected';
      }
    }
    return checkAndFormatCurrency(formatCurrencyForLocale(item.shipping));
  };

  const formatPhoneNumber = (phone, country) => {
    if (country === 'US') {
      const cleaned = (` ${phone}`).replace(/\D/g, '');
      const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        return `(${match[1]}) ${match[2]}-${match[3]}`;
      }
      return phone;
    }

    return phone;
  };

  const getTotalFees = () => {
    const ship = parseFloat(preformatFloat(invoices.invoiceList[0].shipHandle));
    const insurance = parseFloat(preformatFloat(invoices.invoiceList[0].insurance));
    const other = parseFloat(preformatFloat(invoices.invoiceList[0].misc));
    const tax = parseFloat(preformatFloat(invoices.invoiceList[0].tax));
    const fees = ship + insurance + other + tax;

    return fees.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getTotalAdditionalFees = () => {
    const ship = parseFloat(preformatFloat(invoices.invoiceList[0].totalAdditionalCharges.shipping));
    const insurance = parseFloat(preformatFloat(invoices.invoiceList[0].totalAdditionalCharges.insurance));
    const other = parseFloat(preformatFloat(invoices.invoiceList[0].totalAdditionalCharges.other));
    const tax = parseFloat(preformatFloat(invoices.invoiceList[0].totalAdditionalCharges.tax));
    const fees = ship + insurance + other + tax;

    return fees.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const additionalTotalByItem = (ship, ins, other, tax) => {
    const itemTotal = parseFloat(ship) + parseFloat(ins) + parseFloat(other) + parseFloat(tax);
    return itemTotal;
  };

  const originalChargesPaid = () => {
    const returnFlag = items.every(item => item.paid || item.restricted);
    return returnFlag;
  };

  const getChannelName = (channelID) => {
    let channelName = 'Invaluable';
    if (Number(channelID) === 3) {
      channelName = 'Private Label';
    } else if (Number(channelID) === 4) {
      channelName = 'AuctionZip';
    } else if (Number(channelID) === 9) {
      channelName = 'Floor Bidder';
    }

    return channelName;
  };

  return (

    <Row className="border py-3 mb-5">
      <Col xs={12}>
        <h2 className="mt-2">
          Invoice# {invoiceID}
        </h2>

        {auctions.map((auction) => (
          <div key={auction.title} className="mt-3">
            <div className="font-weight-bold">
              {auction.title}
            </div>
            <div>
              {auction.date}
            </div>
          </div>
        ))}
      </Col>

      <Col xs={12} className="mt-4">
        <Row>
          <Col xs={4} className="pr-2">
            <div className="border invoice-tri-box">
              <div className="border-bottom font-weight-bold p-2">
                {invoices.invoiceList[0].pickup ? 'Bidder Address' : 'Ship To'}
              </div>
              <div className="p-2">
                <div>
                  <a href={`${cfPath()}/al/bidders/viewBidder.cfm?mbrID=${invoices.invoiceList[0].memberID}`} target="_blank" rel="noopener noreferrer">
                    {invoices.invoiceList[0].billing.firstName} {invoices.invoiceList[0].billing.lastName}
                  </a>
                  <span className="ml-1">({getChannelName(invoices.invoiceList[0].companyId)})</span>
                </div>
                <div>
                  <div>{escapeSpecialChars(invoices.invoiceList[0].billing.street)}</div>
                  <div>{escapeSpecialChars(invoices.invoiceList[0].billing.street2)}</div>
                </div>
                <div>
                  {invoices.invoiceList[0].billing.city},&nbsp;
                  {invoices.invoiceList[0].billing.state}&nbsp;
                  {invoices.invoiceList[0].billing.postalCode},&nbsp;
                  {invoices.invoiceList[0].billing.country}
                </div>
                <div className="mt-2">
                  {invoices.invoiceList[0].emailTo}
                </div>
                {invoices.invoiceList[0].billing.phone && (
                  <div>
                    phone: {formatPhoneNumber(invoices.invoiceList[0].billing.phone, invoices.invoiceList[0].billing.country)}
                  </div>
                )}
              </div>
            </div>
          </Col>
          <Col xs={4} className="px-2">
            <div className="border invoice-tri-box">
              <div className="border-bottom font-weight-bold p-2">
                Auction House
              </div>
              <div className="p-2">
                <div className="">
                  {house.name}
                </div>
                <div className="text-capitalize mt-0">
                  {houseAddress.street1} {houseAddress.street2}<br />
                  {houseAddress.city}, {houseAddress.stateCountryCompositeKey.stateCode}{houseAddress.province} &nbsp;
                  {houseAddress.postCode}, {houseAddress.stateCountryCompositeKey.countryCode}
                </div>
                <div className="mt-2">
                  {house.email}
                </div>
                <div className="mt-0">
                  {houseAddress.telNo}
                </div>
              </div>
            </div>
          </Col>
          <Col xs={4} className="pl-2">
            <div className="border invoice-tri-box">
              <div className="mt-2">
                <div className="float-left">
                  Invoice Total
                </div>
                <div className="float-right">
                  {checkAndFormatCurrency(formatCurrencyForLocale(total))}
                </div>
                <div className="clear-both" />
              </div>
              <div className="mt-2">
                <div className="float-left">
                  Payment(s) Made
                </div>
                <div className="float-right">
                  ({checkAndFormatCurrency(formatCurrencyForLocale(payments))})
                </div>
                <div className="clear-both" />
              </div>
              <div className="mt-2">
                <div className="float-left font-weight-bold">
                  Total Amount Due
                </div>
                <div className="float-right">
                  {checkAndFormatCurrency(formatCurrencyForLocale(due))}
                </div>
                <div className="clear-both" />
              </div>
              <div className="mt-3 pt-3 border-top">
                <div className="float-left">
                  {paymentProcessingEnabled ? 'Amount Payable Online' : 'Contact Seller to Pay'}
                </div>
                <div className="float-right">
                  {paymentProcessingEnabled
                    ? checkAndFormatCurrency(formatCurrencyForLocale(onlineTotal))
                    : checkAndFormatCurrency(formatCurrencyForLocale(due))}
                </div>
                <div className="clear-both" />
              </div>
              {paymentProcessingEnabled && invoices.invoiceList[0].restrictedTotal !== '0.00' && (
                <div className="mt-2">
                  <div className="float-left">
                    Contact Seller to Pay
                  </div>
                  <div className="float-right">
                    {checkAndFormatCurrency(formatCurrencyForLocale(invoices.invoiceList[0].restrictedTotal))}
                  </div>
                  <div className="clear-both" />
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Col>
      {invoices.invoiceList[0].pickup && (
        <Col xs={12}>
          <div className="mt-1 ml-2 font-italic font-weight-bold">
            * Please note: this invoice has been marked as a pickup invoice
          </div>
        </Col>
      )}
      <Col xs={12} className="mt-5">
        <Table bordered className="invoice-table">
          <thead>
            <tr>
              <th>Lot Description</th>
              <th>Hammer</th>
              <th>BP</th>
              <th>Shipping</th>
              <th>Insurance</th>
              <th>Other</th>
              <th>Tax</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {unsuppressedItems.map((item) => (
              <tr key={`${item.lotRef}_${Math.random()}`}>
                <td className="p-1 invoice-title-block">
                  {/* Lot Description value */}
                  <Table borderless className="mb-0 p-0">
                    <tbody>
                      <tr>
                        <td className="invoice-lot-image" valign="bottom">
                          <img
                            src={item.imgUrls[0] ? `${awsServer}${item.imgUrls[0]}` : noImage}
                            className={item.imgUrls[0] ? '' : 'border'}
                            alt={item.title}
                            loading="lazy"
                          />
                        </td>
                        <td className="invoice-title-cell">
                          {paymentProcessingEnabled && item.restricted && ('* ')}
                          Lot {item.lotNumber}: {item.title}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </td>
                <td className="invoice-number-cell">
                  {checkAndFormatCurrency(formatCurrencyForLocale(item.hammerTotal))}
                </td>
                <td className="invoice-number-cell">
                  {checkAndFormatCurrency(formatCurrencyForLocale(item.premiumTotal))}
                </td>
                <td className="invoice-shipping-cell invoice-number-cell">
                  {showCostsPerItem && getShippingMethod(item)}
                </td>
                <td className="invoice-number-cell">
                  {showCostsPerItem && checkAndFormatCurrency(formatCurrencyForLocale(item.insurance))}
                </td>
                <td className="invoice-number-cell">
                  {showCostsPerItem && checkAndFormatCurrency(formatCurrencyForLocale(item.misc))}
                </td>
                <td className="invoice-number-cell">
                  {showCostsPerItem && checkAndFormatCurrency(formatCurrencyForLocale(item.tax))}
                </td>
                <td className="invoice-total-cell">
                  {checkAndFormatCurrency(formatCurrencyForLocale(item.total))}
                  {item.paid && (
                  <span className="invoice-badge badge-dark ml-1 small">paid</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {showLotAddlCharges && (
          <Table bordered className="invoice-table">
            <thead>
              <tr>
                <th>Additional Charges</th>
                <th> S/H</th>
                <th>Insurance</th>
                <th>Other</th>
                <th>Tax</th>
                <th>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                item.additionalPriceInfo !== null && (
                <tr key={`charges${item.lotRef}_${Math.random()}`}>
                  <td>
                    <div className="invoice-desc-cell">
                      {paymentProcessingEnabled && item.restricted && ('* ')}
                      Lot {item.lotNumber}: {item.title}
                    </div>
                  </td>
                  <td className="invoice-number-cell">
                    {checkAndFormatCurrency(formatCurrencyForLocale(item.additionalPriceInfo.shipping))}
                  </td>
                  <td className="invoice-number-cell">
                    {checkAndFormatCurrency(formatCurrencyForLocale(item.additionalPriceInfo.insurance))}
                  </td>
                  <td className="invoice-number-cell">
                    {checkAndFormatCurrency(formatCurrencyForLocale(item.additionalPriceInfo.other))}
                  </td>
                  <td className="invoice-number-cell">
                    {checkAndFormatCurrency(formatCurrencyForLocale(item.additionalPriceInfo.tax))}
                  </td>
                  <td className="invoice-total-cell">
                    {formatCurrencyForLocale(additionalTotalByItem(
                      item.additionalPriceInfo.shipping,
                      item.additionalPriceInfo.insurance,
                      item.additionalPriceInfo.other,
                      item.additionalPriceInfo.tax
                    ))}
                    {item.additionalPriceInfo.paid && (
                      <span className="invoice-badge badge-dark ml-1 small">paid</span>
                    )}

                  </td>
                </tr>
                )
              ))}
            </tbody>
          </Table>
        )}
        {!showLotAddlCharges && !showCostsPerItem && (
          <Table bordered className="invoice-table fees-row mt-4">
            <tbody>
              <tr>
                <td className="font-weight-bold invoice-total-taxes-block">
                  Total Taxes & Fees
                </td>
                <td className="invoice-number-cell">
                  {checkAndFormatCurrency(formatCurrencyForLocale(invoices.invoiceList[0].shipHandle))}
                </td>
                <td className="invoice-number-cell">
                  {checkAndFormatCurrency(formatCurrencyForLocale(invoices.invoiceList[0].insurance))}
                </td>
                <td className="invoice-number-cell">
                  {checkAndFormatCurrency(formatCurrencyForLocale(invoices.invoiceList[0].misc))}
                </td>
                <td className="invoice-number-cell">
                  {checkAndFormatCurrency(formatCurrencyForLocale(invoices.invoiceList[0].tax))}
                </td>
                <td className="invoice-total-cell">
                  {checkAndFormatCurrency(formatCurrencyForLocale(getTotalFees()))}
                  {(originalChargesPaid() || invoices.invoiceList[0].allPaid) && (
                    <span className="invoice-badge badge-dark ml-1 small">paid</span>
                  )}
                </td>
              </tr>
            </tbody>
          </Table>
        )}
        {invoices.invoiceList[0].additionalCharge && invoices.invoiceList[0].additionalPriceInfo !== null && !isRestricted && (
          <Table bordered className="invoice-table fees-row mt-4">
            <tbody>
              <tr>
                <td className="font-weight-bold">
                  Total Additional Charges
                </td>
                <td className="invoice-number-cell">
                  {checkAndFormatCurrency(formatCurrencyForLocale(invoices.invoiceList[0].additionalPriceInfo.shipping))}
                </td>
                <td className="invoice-number-cell">
                  {checkAndFormatCurrency(formatCurrencyForLocale(invoices.invoiceList[0].additionalPriceInfo.insurance))}
                </td>
                <td className="invoice-number-cell">
                  {checkAndFormatCurrency(formatCurrencyForLocale(invoices.invoiceList[0].additionalPriceInfo.other))}
                </td>
                <td className="invoice-number-cell">
                  {checkAndFormatCurrency(formatCurrencyForLocale(invoices.invoiceList[0].additionalPriceInfo.tax))}
                </td>
                <td className="invoice-total-cell">
                  {checkAndFormatCurrency(formatCurrencyForLocale(getTotalAdditionalFees()))}
                  {invoices.invoiceList[0].additionalPriceInfo.paid && (
                    <span className="invoice-badge badge-dark ml-1 small">paid</span>
                  )}
                </td>
              </tr>
            </tbody>
          </Table>
        )}
        {paymentProcessingEnabled && isRestricted && (
          <div className="italic-text">
            *this item is restricted and cannot be paid for online
          </div>
        )}
      </Col>
      { isInvShippingAvailable && (
        <Col xs={12} className="mt-4">
          <div className="p-3 border">
            <div className="font-weight-bold">
              Shipping Method(s)
            </div>
            <div className="mt-3 border-top pt-3">
              <span>{defaultShippingMethods}</span>
            </div>
          </div>
        </Col>
      )}
      <Col xs={12} className="mt-4">
        <div className="p-3 border">
          <div className="font-weight-bold">
            Payment Methods
          </div>
          <div className="mt-3 border-top pt-3">
            <span>This seller accepts</span> {invoices.invoiceList[0].acceptedCards.map((card, index) => (
              <span key={`cardID_${card}`}>{cardNameObject[card]}
                {index === invoices.invoiceList[0].acceptedCards.length - 1 ? '' : ', '}
              </span>
            ))}
            {invoices.invoiceList[0].paypal.url && (', Paypal')}
            {invoices.invoiceList[0].allowACHPayment && (', ACH')}
            <span>.</span>
          </div>
        </div>
      </Col>
      {invoiceMessages && (
        invoiceMessages.invoiceMessages.map(message => (
          <Col xs={12} className="mt-4" key={message.index}>
            <div className="p-3 border">
              <div className="font-weight-bold text-capitalize">
                {message.headerLC}
              </div>
              <div className="mt-3 border-top pt-3">
                {message.detail}
              </div>
            </div>
          </Col>
        ))
      )}
    </Row>
  );
};
export default ViewInvoice;
